import React, { useEffect, useMemo, useState } from "react";
import { Oval } from "react-loader-spinner";
import { openVideoList, openStatVideo } from "../../helpers/service";


// Media Query
import { useMediaQuery } from "react-responsive";
import { breakpoints } from "../../style/size";

// Language
import { useTranslation } from "react-i18next";
import {
  BottomCardsWrap,
  Cards,
  CardsWrap,
  InnerContent,
  LoadingState,
  OverviewCard,
  OverviewWrap,
  StatsCard,
  TopCardInfo,
  TopCardsWrap,
  VideoContent,
  VideoInfo,
  VideoItems,
  VideoList,
  VideoStats,
  WrappBottomContent,
  WrappContent,
  LoadingVideo,
} from "../../style/Overview";

// Images
import arrowIconUp from "../../images/up.svg";
import arrowIconDown from "../../images/down.svg";
import play from "../../images/ic_play-button.svg";
import playBlue from "../../images/play_blue.svg";
import { useNavigate } from "react-router-dom";
import { EmptyCard } from "../../style/Global";
import { getPlayerParamMarkers, getTeamParamMarkers } from "../../helpers";
import { NotificationList } from "../notification/NotificationList";
import { useNotification } from "../notification/useNotification";
import Matches from "./Matches";
import { DropdownWrapper, OpenedDropdown } from "./MatchTable";
import TextInput from "../inputs/TextInput";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useLexicon } from "../../hooks/useLexicon";
import { useParameterLexicon } from "../../hooks/useParameterLexicon";
import {
  getOverviewStatDisplayCount,
  getOverviewVideoCount,
  mergeOverviewVideoParameters,
  normalizeOverviewMarkerIds,
} from "./overviewVideoMarkers";

const Overview = ({
  table,
  leaders,
  loadingList,
  statsLoading = false,
  id,
  handleSelectChange,
  selectedSeason,
  teamInfo,
  loadingStatMatch,
  matchesTable = [],
}) => {
  const l = useLexicon();
  const { getTeamParameterLexiconId } = useParameterLexicon();
  const [openChangeSeason, setOpenChangeSeason] = useState(false);
  const [expandedIndices, setExpandedIndices] = useState([]);
  const overviewStatsLoading = loadingList || statsLoading;

  const currentSelectedSeason = useMemo(() => {
    if (table?.available_seasons?.length > 0) {
      const foundedSeason = table?.available_seasons?.find((item) => item.id == selectedSeason);
      if (selectedSeason && foundedSeason) {
        return foundedSeason?.years;
      } else {
        return table?.available_seasons?.[0]?.years;
      }
    }
    return l(992) || "Select Season";
  }, [selectedSeason, table?.available_seasons, l]);

  const navigate = useNavigate();

  // Match ids backing the currently displayed stats — used to lazily fetch the
  // markers for a clicked stat (the overview payload no longer inlines markers).
  const selectedMatchIds = useMemo(
    () =>
      (matchesTable || [])
        .map((match) => match?.id)
        .filter((matchId) => matchId !== null && matchId !== undefined),
    [matchesTable]
  );

  const { notifications, addNotification, removeNotification } = useNotification();

  const toggleShowLeads = (index) => {
    setExpandedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  //new params
  // first green section
  const ShotsOnTarget = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 24);
      return value;
    }
    return null
  }, [leaders])

  const ShotsOnTargetWide = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 26);
      return value;
    }
    return null
  }, [leaders])

  const CounterAttacks = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 196);
      return value;
    }
    return null
  }, [leaders])

  const Corners = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 146);
      return value;
    }
    return null
  }, [leaders])

  //second green section
  const FreeKicks = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 147);
      return value;
    }
    return null
  }, [leaders])

  const Penalties = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 190);
      return value;
    }
    return null
  }, [leaders])

  const Crosses = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 49);
      return value;
    }
    return null
  }, [leaders])

  const Dribblings = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 90);
      return value;
    }
    return null
  }, [leaders])

  //first red section
  const OppCounterAttacks = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 196);
      return value;
    }
    return null
  }, [leaders])

  const OppCorners = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 146);
      return value;
    }
    return null
  }, [leaders])

  const OppFreeKicks = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 147);
      return value;
    }
    return null
  }, [leaders])

  const OppPenalties = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 190);
      return value;
    }
    return null
  }, [leaders])

  //second red section
  const graveMistakes = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 143);
      return value;
    }
    return null
  }, [leaders])

  const arialDuels = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 105);
      return value;
    }
    return null
  }, [leaders])

  const ballInterception = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 130);
      return value;
    }
    return null
  }, [leaders])

  const ballRecoveries = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 136);
      return value;
    }
    return null
  }, [leaders])

  // new params

  const GoalsScored = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 1);
      return value;
    }
    return null
  }, [leaders])

  const GoalOpportunities = useMemo(() => {
    if (leaders?.in_attack?.length > 0) {
      const value = leaders?.in_attack?.find(item => item.param_id === 4);
      const value2 = leaders?.in_attack?.find(item => item.param_id === 6);
      return mergeOverviewVideoParameters(value, value2);
    }
    return null
  }, [leaders])

  const OppGoalOpportunities = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 6);
      return value;
    }
    return null
  }, [leaders])


  const GoalsConceeded = useMemo(() => {
    if (leaders?.in_defence?.length > 0) {
      const value = leaders?.in_defence?.find(item => item.param_id === 2);
      return value;
    }
    return null
  }, [leaders])

  // const GoalOppSum = useMemo(() => {
  //   return (GoalsUnsuccessfull?.count || 0) + (GoalsScored?.count || 0)
  // }, [GoalsUnsuccessfull, GoalsScored])

  const handleClickPlayerParam = async (param, player) => {
    const { parameter_id: param_id } = param;
    const { player: { id: player_id, display_name }, count } = player;
    try {
      const controller = new AbortController();
      addNotification(`Fetching clips. Please don't close or refresh the page till the download is completed.`, "info", controller);
      document.body.classList.add('loading-cursor');
      const data = await getPlayerParamMarkers(player_id, param_id, selectedSeason, controller);
      // Scope the clips to the matches shown in this team's overview. The player
      // markers endpoint returns the player's events for the whole season —
      // including other competitions and matches played under a different team
      // id — whereas the leader count is scoped to this team's matches. Without
      // this filter the opened clip count exceeds the displayed value (e.g.
      // table 12 vs "18 Events"). Fall back to all results if we have no match
      // set to filter against.
      const matchIdSet = new Set((selectedMatchIds || []).map((matchId) => Number(matchId)));
      const scopedResults =
        matchIdSet.size > 0
          ? (data?.results || []).filter((item) => matchIdSet.has(Number(item?.match_id)))
          : (data?.results || []);
      const markerIds = normalizeOverviewMarkerIds(scopedResults);
      if (markerIds.length > 0) {
        // Use team parameter display name for team leaders section
        const displayName = getTeamParameterDisplayName(param);
        openVideoList(markerIds.join(','), `${displayName} - ${display_name}`)
      } else {
        addNotification(`There are 0 clips in the response from the server.`, 'warning')
      }
    } catch (e) {
      console.log('error get param markers', e);
      if (e.type === 'canceled') {
        addNotification(`The request to receive ${count} clips of the player has been canceled.`, 'warning')
        return
      }
      addNotification(`Error in receiving ${count} player clips.`, 'error')
    } finally {
      document.body.classList.remove('loading-cursor');
    }
  }

  const handleClickTeamParam = async (param) => {
    const { id: param_id, count } = param;
    try {
      const controller = new AbortController();
      addNotification(`Fetching clips. Please don't close or refresh the page till the download is completed.`, "info", controller);
      document.body.classList.add('loading-cursor');
      const data = await getTeamParamMarkers(id, param_id, 'in_attack', selectedSeason, controller);
      const markerIds = normalizeOverviewMarkerIds(data?.results);
      if (markerIds.length > 0) {
        const displayName = getTeamParameterDisplayName(param);
        openVideoList(markerIds.join(','), `${displayName} - ${table?.team?.name}`)
      } else {
        addNotification(`There are 0 clips in the response from the server.`, 'warning')
      }
    } catch (e) {
      console.log('error get param markers', e);
      if (e.type === 'canceled') {
        addNotification(`The request to receive ${count} clips of the team has been canceled.`, 'warning')
        return
      }
      addNotification(`Error in receiving team events. ${e.message}`, 'error')
    } finally {
      document.body.classList.remove('loading-cursor');
    }
  }

  const isCanClickPlayerStat = (leader, item) => {
    // markers are now lazy-loaded on click, so clickability is gated on the
    // count alone (no inline markers array to measure anymore).
    return leader?.count === 0
      || item.parameter_name.includes('%')
      || item.parameter_name.includes('Mins Played')
  }

  const getTeamParameterDisplayName = (parameterInput) => {
    const fallbackName =
      typeof parameterInput === 'string'
        ? parameterInput
        : parameterInput?.parameter_name || parameterInput?.name || '';
    if (!fallbackName) return '';
    const lexiconId = getTeamParameterLexiconId(parameterInput);
    if (lexiconId) {
      return l(lexiconId) || fallbackName;
    }
    return fallbackName;
  };

  const isCanClickTeamStat = (item) => {
    // markers are now lazy-loaded on click — gate on the (still-present) count.
    return getOverviewVideoCount(item) === 0
      || item.name.includes('%')
      || item.name.includes('Mins Played')
      || item.name.includes('Passes')
  }

  const formatNumber = (number) => {
    return new Intl.NumberFormat("en-US").format(number);
  };

  const formatVideoCount = (parameter) =>
    formatNumber(getOverviewVideoCount(parameter));

  // markers are no longer inlined in the overview payload; fetch them on demand
  // for this stat. `opponent` is true only for the Opponent's-* defence cards,
  // whose param ids also appear (as own-team stats) in the attack section.
  const openOverviewVideoList = (parameter, title, opponent = false) => {
    const paramId =
      parameter?.param_id ?? parameter?.parameter_id ?? parameter?.id;
    openStatVideo(
      { kind: "team", id, matchIds: selectedMatchIds, paramId, opponent },
      title
    );
  };

  const changeSeasonDropdownRef = useOutsideClick(() => {
    if (openChangeSeason) {
      setOpenChangeSeason(false);
    }
  });


  return (
    <OverviewWrap>
      <TopCardsWrap>
        <WrappContent>
          <TopCardInfo>
            <h2>{l(8)}</h2>
            {/* <SelectDropdown>
              <select onChange={handleSelectChange} value={selectedSeason}>
                {table.available_seasons?.map((item, index) => {
                  return (
                    <option key={index} value={item.id}>
                      Season {item.years}
                    </option>
                  );
                })}
              </select>
            </SelectDropdown> */}
          </TopCardInfo>
          <OverviewCard>
            {overviewStatsLoading ? (
              <LoadingState style={{ width: "100%", height: "100%" }}>
                <Oval
                  visible={true}
                  height="80"
                  width="80"
                  secondaryColor={
                    localStorage.getItem("theme") === "light"
                      ? "#4E5255"
                      : "#CFDAE6"
                  }
                  color={
                    localStorage.getItem("theme") === "light"
                      ? "#2363F7"
                      : "#4fa94d"
                  }
                  ariaLabel="oval-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                />
              </LoadingState>
            ) : (
              <InnerContent>
                {leaders && Object.keys(leaders.main_stats).length > 0 ? (
                  <>
                    {leaders.main_stats.map((item, index) => {
                      if (item.id === 144 || item.id === 36) return // Remove grave mistakes always 0
                      const displayName = getTeamParameterDisplayName(item);
                      return (
                        <div key={index} className="statistics statsPadding">
                          <h5>{displayName}:</h5>
                          <h4
                            style={
                              isCanClickTeamStat(item)
                                ? { cursor: "not-allowed" }
                                : { cursor: "pointer" }
                            }
                            onClick={() => {
                              if (!isCanClickTeamStat(item)) {
                                openOverviewVideoList(item, `${displayName} - ${table?.team?.name}`)
                              }
                            }}
                            onContextMenu={() => {
                              if (!isCanClickTeamStat(item)) {
                                openOverviewVideoList(item, `${displayName} - ${table?.team?.name}`)
                              }
                            }}
                          >
                            {formatNumber(getOverviewStatDisplayCount(item))}
                          </h4>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <EmptyCard>
                    <h2>{l(464)}</h2>
                  </EmptyCard>
                )}
              </InnerContent>
            )}
          </OverviewCard>
        </WrappContent>
        {loadingStatMatch ? (
          <LoadingState>
            <Oval
              visible={true}
              height="80"
              width="80"
              secondaryColor={
                localStorage.getItem("theme") === "light"
                  ? "#4E5255"
                  : "#CFDAE6"
              }
              color={
                localStorage.getItem("theme") === "light"
                  ? "#2363F7"
                  : "#4fa94d"
              }
              ariaLabel="oval-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </LoadingState>
        ) : (
          <Matches
            table={matchesTable}
            type={'team'}
            styleTableWrapper={{
              height: '300px',
              overflowY: 'scroll',
              scrollbarWidth: 'auto',
            }}
            showTitle={true}
            season={selectedSeason}
            playerTeams={table?.team ? [table.team] : []}
            controlled
            hideFilterButton
          />
        )}
      </TopCardsWrap>
      <>
        {
          loadingList ? (
            <LoadingState>
              <Oval
                visible={true}
                height="80"
                width="80"
                secondaryColor={
                  localStorage.getItem("theme") === "light"
                    ? "#4E5255"
                    : "#CFDAE6"
                }
                color={
                  localStorage.getItem("theme") === "light"
                    ? "#2363F7"
                    : "#4fa94d"
                }
                ariaLabel="oval-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </LoadingState>
          ) : (
            <WrappContent style={{ marginBottom: 30 }}>
              <TopCardInfo>
                <h2>{l(1060)}</h2>
                {/* <SelectDropdown>
                  <select onChange={handleSelectChange} value={selectedSeason}>
                    {table.available_seasons?.map((item, index) => {
                      return (
                        <option key={index} value={item.id}>
                          Season {item.years}
                        </option>
                      );
                    })}
                  </select>
                </SelectDropdown> */}
                {/* <DropdownWrapper ref={changeSeasonDropdownRef}>
                  <div
                    style={{ position: "relative" }}
                    onClick={() => setOpenChangeSeason(!openChangeSeason)}
                  >
                    <TextInput
                      type="text"
                      value={currentSelectedSeason}
                      readOnly
                    />

                    <img
                      src={openChangeSeason ? arrowIconUp : arrowIconDown}
                      alt={arrowIconDown}
                      style={{
                        color: "#fff",
                        position: "absolute",
                        right: 0,
                        cursor: "pointer",
                      }}
                    />
                  </div>

                  {openChangeSeason && (
                    <OpenedDropdown>
                      {table?.available_seasons?.map((item) => (
                        <div key={item.id} onClick={() => handleSelectChange({ target: { value: item.id } })}>
                          {item.years}
                        </div>
                      ))}
                    </OpenedDropdown>
                  )}
                </DropdownWrapper> */}
              </TopCardInfo>
              <OverviewCard style={{ height: 400 }}>
                <InnerContent>
                  <CardsWrap>
                    {leaders?.team_leaders.length > 0 ? (
                      leaders?.team_leaders?.filter(item => item.parameter_id !== 111 && item.parameter_id !== 153 && item.parameter_id !== 176 && item.parameter_id !== 181 && item.parameter_id !== 182)?.sort((a, b) => a.parameter_id - b.parameter_id).map((item, index) => {
                        const displayName = getTeamParameterDisplayName(item);
                        return (
                        <Cards key={index}>
                          <h3 title={displayName}>{displayName}</h3>
                          {item?.leaders.slice(0, 5).map((leader, idx) => (
                            <div key={idx} className="statistics">
                              <h5
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  navigate(
                                    `/tournaments/player/${leader?.player?.id}`
                                  )
                                }
                              >
                                {leader?.player?.display_name}:
                              </h5>
                              <h4
                                style={
                                  isCanClickPlayerStat(leader, item)
                                    ? { cursor: "not-allowed" }
                                    : { cursor: "pointer" }
                                }
                                onClick={() => {
                                  if (!isCanClickPlayerStat(leader, item)) {
                                    handleClickPlayerParam(item, leader);
                                  }
                                }
                                }
                                onContextMenu={() => {
                                  if (!isCanClickPlayerStat(leader, item)) {
                                    handleClickPlayerParam(item, leader);
                                  }
                                }}
                              >
                                {leader?.count}
                              </h4>
                            </div>
                          ))}
                          {item?.leaders?.length >= 5 && (
                            <button onClick={() => toggleShowLeads(index)}>
                              {expandedIndices.includes(index)
                                ? "Show less"
                                : "Show more"}
                            </button>
                          )}
                          {expandedIndices.includes(index) && (
                            <>
                              {item?.leaders.slice(5).map((leader, idx) => (
                                <div key={idx} className="statistics">
                                  <h5
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      navigate(
                                        `/tournaments/player/${leader?.player?.id}`
                                      )
                                    }
                                  >
                                    {leader?.player?.display_name}:
                                  </h5>
                                  <h4
                                    style={
                                      isCanClickPlayerStat(leader, item)
                                        ? { cursor: "not-allowed" }
                                        : { cursor: "pointer" }
                                    }
                                    onClick={() =>
                                      !isCanClickPlayerStat(leader, item) &&
                                      handleClickPlayerParam(item, leader)
                                    }
                                    onContextMenu={() => {
                                      !isCanClickPlayerStat(leader, item) &&
                                      handleClickPlayerParam(item, leader)
                                    }}
                                  >
                                    {leader?.count}
                                  </h4>
                                </div>
                              ))}
                            </>
                          )}
                        </Cards>
                        );
                      })
                    ) : (
                      <EmptyCard>
                        <h2>{l(464)}</h2>
                      </EmptyCard>
                    )}
                  </CardsWrap>
                </InnerContent>
              </OverviewCard>
            </WrappContent>
          )
        }
      </>


      <BottomCardsWrap>
        <WrappBottomContent>
          <TopCardInfo>
            <h2>{table?.team?.name} {l(989)}</h2>
          </TopCardInfo>
          <OverviewCard
            style={{ height: "fit-content", scrollbarWidth: "none" }}
          >
            <InnerContent>
              <VideoContent>
                <VideoStats>
                  <StatsCard
                    onClick={() => {
                      openOverviewVideoList(GoalsScored, `${l(102)} - ${table?.team?.name}`)
                    }}
                    onContextMenu={() => {
                      openOverviewVideoList(GoalsScored, `${l(102)} - ${table?.team?.name}`)
                    }}
                  >
                    <h4
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#fff",
                      }}
                    >
                      {formatVideoCount(GoalsScored)}
                    </h4>
                    <h5
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#36e82d",
                      }}
                    >
                      {l(102)}
                    </h5>
                    <img
                      src={
                        localStorage.getItem("theme") === "light"
                          ? playBlue
                          : play
                      }
                      width={40}
                      alt={play}
                      style={{ cursor: "pointer", position: 'absolute', right: 20, top: '10%' }}
                    />
                  </StatsCard>

                  {overviewStatsLoading ? (
                    <LoadingVideo>
                      <Oval
                        visible={true}
                        height="80"
                        width="80"
                        secondaryColor={
                          localStorage.getItem("theme") === "light"
                            ? "#4E5255"
                            : "#CFDAE6"
                        }
                        color={
                          localStorage.getItem("theme") === "light"
                            ? "#2363F7"
                            : "#4fa94d"
                        }
                        ariaLabel="oval-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                      />
                    </LoadingVideo>
                  ) : (
                    <VideoList>
                      {ShotsOnTarget && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(ShotsOnTarget, `${l(462)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(ShotsOnTarget, `${l(462)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(462)}</h3>{" "}
                            <span>{formatVideoCount(ShotsOnTarget)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {ShotsOnTargetWide && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(ShotsOnTargetWide, `${l(570)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(ShotsOnTargetWide, `${l(570)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(570)}</h3>{" "}
                            <span>{formatVideoCount(ShotsOnTargetWide)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {CounterAttacks && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(CounterAttacks, `${l(760)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(CounterAttacks, `${l(760)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(760)}</h3>{" "}
                            <span>{formatVideoCount(CounterAttacks)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {Corners && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(Corners, `${l(461)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(Corners, `${l(461)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(461)}</h3>{" "}
                            <span>{formatVideoCount(Corners)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                    </VideoList>
                  )}
                </VideoStats>

                <VideoStats>
                  <StatsCard
                    onClick={() => {
                      openOverviewVideoList(GoalOpportunities, `${l(429)} - ${table?.team?.name}`)
                    }}
                    onContextMenu={() => {
                      openOverviewVideoList(GoalOpportunities, `${l(429)} - ${table?.team?.name}`)
                    }}
                  >
                    <h4
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#fff",
                      }}
                    >
                      {formatVideoCount(GoalOpportunities)}
                    </h4>
                    <h5
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#36e82d",
                      }}
                    >
                      {l(429)}
                    </h5>
                    <img
                      src={
                        localStorage.getItem("theme") === "light"
                          ? playBlue
                          : play
                      }
                      width={40}
                      alt={play}
                      style={{ cursor: "pointer", position: 'absolute', right: 20, top: '10%' }}
                    />
                  </StatsCard>

                  {overviewStatsLoading ? (
                    <LoadingVideo>
                      <Oval
                        visible={true}
                        height="80"
                        width="80"
                        secondaryColor={
                          localStorage.getItem("theme") === "light"
                            ? "#4E5255"
                            : "#CFDAE6"
                        }
                        color={
                          localStorage.getItem("theme") === "light"
                            ? "#2363F7"
                            : "#4fa94d"
                        }
                        ariaLabel="oval-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                      />
                    </LoadingVideo>
                  ) : (
                    <VideoList>
                      {FreeKicks && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(FreeKicks, `${l(731)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(FreeKicks, `${l(731)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(731)}</h3>{" "}
                            <span>{formatVideoCount(FreeKicks)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {Penalties && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(Penalties, `${l(756) || 'Penalties'} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(Penalties, `${l(756) || 'Penalties'} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(756) || 'Penalties'}</h3>{" "}
                            <span>{formatVideoCount(Penalties)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {Crosses && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(Crosses, `${l(587)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(Crosses, `${l(587)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(587)}</h3>{" "}
                            <span>{formatVideoCount(Crosses)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {Dribblings && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(Dribblings, `${l(63)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {  
                            openOverviewVideoList(Dribblings, `${l(63)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(63)}</h3>{" "}
                            <span>{formatVideoCount(Dribblings)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                    </VideoList>
                  )}
                </VideoStats>
              </VideoContent>
            </InnerContent>
          </OverviewCard>
        </WrappBottomContent>

        <WrappBottomContent>
          <TopCardInfo>
            <h2>{table?.team?.name} {l(990)}</h2>
          </TopCardInfo>
          <OverviewCard
            style={{ height: "fit-content", scrollbarWidth: "none" }}
          >
            <InnerContent>
              <VideoContent>
                <VideoStats>
                  <StatsCard
                    className="defense"
                    onClick={() => {
                      openOverviewVideoList(GoalsConceeded, `${l(318)} - ${table?.team?.name}`)
                    }}
                    onContextMenu={() => {
                      openOverviewVideoList(GoalsConceeded, `${l(318)} - ${table?.team?.name}`)
                    }}
                  >
                    <h4
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#fff",
                      }}
                    >
                      {formatVideoCount(GoalsConceeded)}
                    </h4>
                    <h5
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#F20A46",
                      }}
                    >
                      {l(318)}
                    </h5>
                    <img
                      src={
                        localStorage.getItem("theme") === "light"
                          ? playBlue
                          : play
                      }
                      width={40}
                      alt={play}
                      style={{ cursor: "pointer", position: 'absolute', right: 20, top: '10%' }}
                    />
                  </StatsCard>

                  {overviewStatsLoading ? (
                    <LoadingVideo>
                      <Oval
                        visible={true}
                        height="80"
                        width="80"
                        secondaryColor={
                          localStorage.getItem("theme") === "light"
                            ? "#4E5255"
                            : "#CFDAE6"
                        }
                        color={
                          localStorage.getItem("theme") === "light"
                            ? "#2363F7"
                            : "#4fa94d"
                        }
                        ariaLabel="oval-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                      />
                    </LoadingVideo>
                  ) : (
                    <VideoList>
                      {OppCounterAttacks && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(OppCounterAttacks, `${l(1062)} - ${table?.team?.name}`, true)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(OppCounterAttacks, `${l(1062)} - ${table?.team?.name}`, true)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(1062)}</h3>{" "}
                            <span>{formatVideoCount(OppCounterAttacks)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {OppCorners && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(OppCorners, `Opponent's Corners - ${table?.team?.name}`, true)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(OppCorners, `Opponent's Corners - ${table?.team?.name}`, true)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>Opponent's Corners</h3>{" "}
                            <span>{formatVideoCount(OppCorners)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {OppFreeKicks && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(OppFreeKicks, `${l(1063)} - ${table?.team?.name}`, true)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(OppFreeKicks, `${l(1063)} - ${table?.team?.name}`, true)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(1063)}</h3>{" "}
                            <span>{formatVideoCount(OppFreeKicks)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {OppPenalties && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(OppPenalties, `${l(1064)} - ${table?.team?.name}`, true)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(OppPenalties, `${l(1064)} - ${table?.team?.name}`, true)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(1064)}</h3>{" "}
                            <span>{formatVideoCount(OppPenalties)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                    </VideoList>
                  )}
                </VideoStats>

                <VideoStats>
                  <StatsCard
                    className="defense"
                    onClick={() => {
                      openOverviewVideoList(OppGoalOpportunities, `${l(1065)} - ${table?.team?.name}`, true)
                    }}
                    onContextMenu={() => {
                      openOverviewVideoList(OppGoalOpportunities, `${l(1065)} - ${table?.team?.name}`, true)
                    }}
                  >
                    <h4
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#fff",
                      }}
                    >
                      {formatVideoCount(OppGoalOpportunities)}
                    </h4>
                    <h5
                      style={{
                        color:
                          localStorage.getItem("theme") === "light"
                            ? "#015ead"
                            : "#F20A46",
                      }}
                    >
                      {l(1065)}
                    </h5>
                    <img
                      src={
                        localStorage.getItem("theme") === "light"
                          ? playBlue
                          : play
                      }
                      width={40}
                      alt={play}
                      style={{ cursor: "pointer", position: 'absolute', right: 20, top: '10%' }}
                    />
                  </StatsCard>

                  {overviewStatsLoading ? (
                    <LoadingVideo>
                      <Oval
                        visible={true}
                        height="80"
                        width="80"
                        secondaryColor={
                          localStorage.getItem("theme") === "light"
                            ? "#4E5255"
                            : "#CFDAE6"
                        }
                        color={
                          localStorage.getItem("theme") === "light"
                            ? "#2363F7"
                            : "#4fa94d"
                        }
                        ariaLabel="oval-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                      />
                    </LoadingVideo>
                  ) : (
                    <VideoList>
                      {graveMistakes && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(graveMistakes, `${l(556)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(graveMistakes, `${l(556)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(556)}</h3>{" "}
                            <span>{formatVideoCount(graveMistakes)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {arialDuels && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(arialDuels, `${l(331)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(arialDuels, `${l(331)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(331)}</h3>{" "}
                            <span>{formatVideoCount(arialDuels)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {ballInterception && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(ballInterception, `${l(335)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(ballInterception, `${l(335)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(335)}</h3>{" "}
                            <span>{formatVideoCount(ballInterception)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                      {ballRecoveries && (
                        <VideoItems
                          onClick={() =>
                            openOverviewVideoList(ballRecoveries, `${l(337)} - ${table?.team?.name}`)
                          }
                          onContextMenu={() => {
                            openOverviewVideoList(ballRecoveries, `${l(337)} - ${table?.team?.name}`)
                          }}
                        >
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? playBlue
                                : play
                            }
                            alt={play}
                            style={{ cursor: "pointer" }}
                          />

                          <VideoInfo>
                            <h3>{l(337)}</h3>{" "}
                            <span>{formatVideoCount(ballRecoveries)} {l(1061)}</span>
                          </VideoInfo>
                        </VideoItems>
                      )}
                    </VideoList>
                  )}
                </VideoStats>
              </VideoContent>
            </InnerContent>
          </OverviewCard>
        </WrappBottomContent>
      </BottomCardsWrap>

      {/* display notifications */}
      <NotificationList
        notifications={notifications}
        onClose={removeNotification}
      />
    </OverviewWrap >
  );
};

export default Overview;
